// One page per native ERP connector (/integrations/:slug). Data in pages/integrations/connectors.ts.
// Written for the IT or operations lead a warm intro forwards it to: what is read, what is written,
// how it connects, what IT provides, how long it takes.
import { useParams } from 'react-router-dom';
import { cta, founders } from '../content';
import { Arrow, Button, Swipe, usePageMeta } from '../ui';
import { CtaBand, KitStyles, PageHero, Section, TwoTone, breadcrumbJsonLd, useJsonLd } from './platform/kit';
import { connectors, type Connector } from './integrations/connectors';
import NotFound from './NotFound';

function Glance({ c }: { c: Connector }) {
  return (
    <div className="pk-card overflow-hidden">
      <div className="flex items-center justify-between gap-4 border-b border-bm-line px-5 py-4 sm:px-6">
        <div className="text-[15px] font-semibold text-bm-indigo">{c.name} at a glance</div>
        <span className="h-2.5 w-2.5 flex-none rounded-full" style={{ background: 'var(--bm-gradient)' }} aria-hidden="true" />
      </div>
      <dl>
        {c.glance.map((g, i) => (
          <div
            key={g.k}
            className="pk-rise grid grid-cols-[84px_1fr] items-baseline gap-3 border-b border-bm-line px-5 py-3.5 last:border-b-0 sm:grid-cols-[120px_1fr] sm:px-6"
            style={{ animationDelay: `${0.15 + i * 0.08}s` }}
          >
            <dt className="text-[13.5px] font-medium text-bm-muted">{g.k}</dt>
            <dd className="text-[15.5px] font-semibold leading-snug text-bm-indigo">{g.v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function Reads({ c }: { c: Connector }) {
  return (
    <>
      <div className="max-w-3xl" data-reveal>
        <TwoTone lead={c.reads.title[0]} rest={c.reads.title[1]} />
        <p className="t-lede mt-6 max-w-2xl">{c.reads.lede}</p>
      </div>
      <div className="mt-12 grid gap-px overflow-hidden rounded-[20px] border border-bm-line bg-bm-line md:grid-cols-2">
        {c.reads.items.map((r, i) => (
          <div
            key={r.what}
            className={`bg-white p-6 sm:p-7 ${i === c.reads.items.length - 1 && i % 2 === 0 ? 'md:col-span-2' : ''}`}
            data-reveal style={{ ['--reveal-delay' as string]: `${(i % 2) * 0.06}s` }}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <h3 className="text-[19px] font-bold tracking-[-0.02em] text-bm-indigo">{r.what}</h3>
              <span className="font-mono text-[12px] text-bm-muted">{r.tables}</span>
            </div>
            <ul className="mt-4 flex flex-wrap gap-1.5" aria-label={`${r.what} fields`}>
              {r.fields.map((f) => (
                <li key={f} className="cn-field">
                  {f}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[15px] leading-relaxed text-bm-muted">{r.note}</p>
          </div>
        ))}
      </div>
      <p className="mt-6 text-[15.5px] font-medium text-bm-indigo" data-reveal>
        {c.reads.notRead}
      </p>
    </>
  );
}

function Writes({ c }: { c: Connector }) {
  return (
    <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
      <div data-reveal>
        <TwoTone lead={c.writes.title[0]} rest={c.writes.title[1]} />
        {c.writes.lede && <p className="t-lede mt-6 max-w-md">{c.writes.lede}</p>}
      </div>
      <div data-reveal style={{ ['--reveal-delay' as string]: '0.06s' }}>
        <ul className="border-t border-bm-line">
          {c.writes.items.map((w, i) => (
            <li key={w.t} className="grid grid-cols-[36px_1fr] gap-3 border-b border-bm-line py-6">
              <span className="font-mono text-[13px] leading-[1.9] text-bm-emerald">0{i + 1}</span>
              <div>
                <div className="text-[18px] font-semibold text-bm-indigo">{w.t}</div>
                <p className="mt-1.5 text-[15.5px] leading-relaxed text-bm-muted">{w.d}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-8 rounded-[18px] bg-white p-6 ring-1 ring-bm-line sm:p-7">
          <div className="text-[15px] font-semibold text-bm-indigo">{c.writes.neverNote}</div>
          <ul className="mt-4 flex flex-wrap gap-2">
            {c.writes.never.map((n) => (
              <li key={n} className="cn-never">
                {n}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Flow({ c }: { c: Connector }) {
  return (
    <div className="cn-flow" data-reveal aria-hidden="true">
      <div className="cn-node">
        <div className="text-[13px] font-medium text-bm-muted">Your company</div>
        <div className="mt-1 text-[19px] font-bold tracking-[-0.02em] text-bm-indigo">{c.name}</div>
        <ul className="mt-3 space-y-1 text-[14px] text-bm-ink/80">
          <li>Scheduled task</li>
          <li>Function library</li>
          <li>Saved queries</li>
        </ul>
      </div>
      <div className="cn-pipe">
        <span className="cn-pipe-line" />
        <span className="cn-pipe-label">HTTPS, scoped API key</span>
      </div>
      <div className="cn-node">
        <div className="text-[13px] font-medium text-bm-muted">Your brand, your data</div>
        <div className="mt-1 text-[19px] font-bold tracking-[-0.02em] text-bm-indigo">BrandedMark</div>
        <ul className="mt-3 space-y-1 text-[14px] text-bm-ink/80">
          <li>Products</li>
          <li>Deliveries, unit by unit</li>
          <li>Scannable codes</li>
        </ul>
      </div>
    </div>
  );
}

function Connects({ c }: { c: Connector }) {
  return (
    <>
      <div className="max-w-3xl" data-reveal>
        <TwoTone lead={c.connects.title[0]} rest={c.connects.title[1]} />
        <p className="t-lede mt-6 max-w-2xl">{c.connects.lede}</p>
      </div>
      <div className="mt-12">
        <Flow c={c} />
      </div>
      <dl className="mt-12 grid gap-x-12 border-t border-bm-line md:grid-cols-2">
        {c.connects.items.map((it) => (
          <div key={it.k} className="border-b border-bm-line py-5" data-reveal>
            <dt className="text-[14.5px] font-semibold text-bm-emerald">{it.k}</dt>
            <dd className="mt-1.5 text-[16px] leading-relaxed text-bm-indigo">{it.v}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}

function Needs({ c }: { c: Connector }) {
  return (
    <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
      <div data-reveal>
        <TwoTone lead={c.needs.title[0]} rest={c.needs.title[1]} />
      </div>
      <ol className="border-t border-bm-line" data-reveal style={{ ['--reveal-delay' as string]: '0.06s' }}>
        {c.needs.items.map((n, i) => (
          <li key={n} className="grid grid-cols-[36px_1fr] gap-3 border-b border-bm-line py-5">
            <span className="font-mono text-[13px] leading-[1.9] text-bm-emerald">0{i + 1}</span>
            <span className="text-[16.5px] leading-relaxed text-bm-indigo">{n}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function Plan({ c }: { c: Connector }) {
  const [lead, ...rest] = c.plan.big.split(' ');
  return (
    <>
      <div data-reveal>
        <div className="cn-big">
          {lead} <Swipe>{rest.join(' ')}</Swipe>
        </div>
        <p className="mt-5 text-[clamp(19px,1.8vw,23px)] font-semibold leading-snug tracking-[-0.015em] text-bm-indigo">{c.plan.line}</p>
      </div>
      <ol className="cn-weeks mt-12">
        {c.plan.weeks.map((w, i) => (
          <li key={w.w} className="cn-week" data-reveal style={{ ['--reveal-delay' as string]: `${i * 0.06}s` }}>
            <span className="cn-week-bar" />
            <div className="font-mono text-[12.5px] text-bm-muted">{w.w}</div>
            <div className="mt-1 text-[21px] font-bold tracking-[-0.02em] text-bm-indigo">{w.t}</div>
            <p className="mt-2 text-[15.5px] leading-relaxed text-bm-muted">{w.d}</p>
          </li>
        ))}
      </ol>
      <p className="mt-8 text-[15.5px] font-medium text-bm-indigo" data-reveal>
        {c.plan.after}
      </p>
    </>
  );
}

function Team({ c }: { c: Connector }) {
  return (
    <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
      <div data-reveal>
        <h2 className="t-h2">
          {c.team.title[0]} <Swipe>{c.team.title[1]}</Swipe>
        </h2>
        <p className="t-lede mt-6 max-w-lg">{c.team.lede}</p>
      </div>
      <dl className="border-t border-bm-line" data-reveal>
        {founders.credentials.map((cr) => (
          <div key={cr.k} className="grid grid-cols-[88px_1fr] items-baseline gap-5 border-b border-bm-line py-6">
            <dt className="text-[14.5px] font-semibold text-bm-emerald">{cr.k}</dt>
            <dd className="text-[clamp(18px,1.7vw,22px)] font-semibold leading-snug tracking-[-0.015em] text-bm-indigo">{cr.v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export default function ConnectorPage() {
  const { slug = '' } = useParams();
  const c = connectors[slug];
  usePageMeta(c ? c.meta.title : 'Page not found', c?.meta.description);
  useJsonLd(
    `bc-integrations-${slug}`,
    c
      ? breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'Integrations', path: '/integrations' },
          { name: c.name, path: `/integrations/${c.slug}` },
        ])
      : null
  );
  if (!c) return <NotFound />;

  return (
    <div className="it cn">
      <KitStyles />
      <style>{`
.v2 .cn-big { font-weight: 800; color: var(--bm-indigo); font-size: clamp(48px, 6.4vw, 92px); line-height: .98; letter-spacing: -0.05em; }
.v2 .cn-field { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12.5px; line-height: 1; padding: 6px 8px; border-radius: 7px; background: var(--bm-paper-alt); color: var(--bm-indigo); box-shadow: inset 0 0 0 1px var(--bm-line); overflow-wrap: anywhere; }
.v2 .cn-never { font-size: 14px; font-weight: 600; padding: 6px 12px; border-radius: 999px; color: var(--bm-indigo); background: var(--bm-paper-alt); box-shadow: inset 0 0 0 1px var(--bm-line); text-decoration: line-through; text-decoration-color: var(--bm-green); text-decoration-thickness: 2px; }
.v2 .cn-flow { display: grid; gap: 0; align-items: stretch; }
.v2 .cn-node { background: #fff; border-radius: 18px; padding: 22px 24px; box-shadow: 0 0 0 1px var(--bm-line), 0 12px 32px -20px rgba(24,19,67,.2); }
.v2 .cn-pipe { position: relative; display: grid; place-items: center; min-height: 72px; }
.v2 .cn-pipe-line { position: absolute; left: 50%; top: 0; bottom: 0; width: 5px; margin-left: -2.5px; border-radius: 5px; background: var(--bm-gradient); }
.v2 .cn-pipe-label { position: relative; background: var(--bm-paper); padding: 4px 10px; border-radius: 999px; font-size: 13px; font-weight: 600; color: var(--bm-indigo); box-shadow: inset 0 0 0 1px var(--bm-line); white-space: nowrap; }
@media (min-width: 768px) {
  .v2 .cn-flow { grid-template-columns: minmax(0,1fr) minmax(180px, .8fr) minmax(0,1fr); }
  .v2 .cn-pipe { min-height: 0; }
  .v2 .cn-pipe-line { left: 0; right: 0; top: 50%; bottom: auto; width: auto; height: 5px; margin: -2.5px 0 0; }
}
.v2 .cn-weeks { display: grid; gap: 28px; }
.v2 .cn-week { position: relative; padding-top: 22px; }
.v2 .cn-week-bar { position: absolute; left: 0; right: 0; top: 0; height: 5px; border-radius: 5px; background: var(--bm-line); overflow: hidden; }
.v2 .cn-week-bar::after { content: ''; position: absolute; inset: 0; background: var(--bm-gradient); }
.v2 .cn-week:nth-child(1) .cn-week-bar::after { right: 75%; }
.v2 .cn-week:nth-child(2) .cn-week-bar::after { right: 50%; }
.v2 .cn-week:nth-child(3) .cn-week-bar::after { right: 25%; }
@media (min-width: 768px) {
  .v2 .cn-weeks { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 24px; }
  .v2 .cn-week-bar::after { right: 0 !important; }
  .v2 .cn-week:not(:last-child) .cn-week-bar { right: -24px; }
}
`}</style>
      <PageHero
        kicker="Integrations"
        crumbs={[
          { label: 'Integrations', href: '/integrations' },
          { label: c.name, href: `/integrations/${c.slug}` },
        ]}
        title={
          <>
            {c.hero.title[0]} <Swipe draw>{c.hero.title[1]}</Swipe>
          </>
        }
        lede={c.hero.lede}
        aside={<Glance c={c} />}
      >
        <Button href={cta.primaryHref}>
          {cta.primary} <Arrow />
        </Button>
        <a href="#read" className="pk-textlink text-[15px] sm:ml-2">
          or see what we read
        </a>
      </PageHero>

      <Section id="read" alt>
        <Reads c={c} />
      </Section>

      <Section id="write">
        <Writes c={c} />
      </Section>

      <Section id="connect" alt>
        <Connects c={c} />
      </Section>

      <Section id="it">
        <Needs c={c} />
      </Section>

      <Section id="plan" alt>
        <Plan c={c} />
      </Section>

      <Section id="team">
        <Team c={c} />
      </Section>

      <CtaBand source={`integrations-${c.slug}`} title={c.close.title} lede={c.close.lede} />
    </div>
  );
}
